import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as apiService from "./service";
import {
  Category,
  SubCategory,
  Package,
  Language,
  ContentType,
  PackageContent,
  License,
  PostLicense,
  PatchLicense,
  PackageRequest,
  CreateStepData,
  LinksResponse,
  LinkDataPost,
  FetchPackagesResponse,
} from "../../../Types/types";

export const fetchCategories = createAsyncThunk(
  "data/fetchCategories",
  async (language: string) => await apiService.fetchCategories(language)
);

export const fetchCategoryById = createAsyncThunk(
  "data/fetchCategoryById",
  async ({ id, locale }: { id: number; locale: string }) => {
    const response = await apiService.fetchCategoryById(id, locale);
    return response;
  }
);

export const deleteCategory = createAsyncThunk(
  "data/deleteCategory",
  async (id: number) => {
    await apiService.deleteCategory(id);
    return id;
  }
);

export const postCategory = createAsyncThunk(
  "data/postCategory",
  async (categoryData: { name: Record<Language, string> }) => {
    const newCategory = await apiService.postCategory(categoryData);
    return newCategory;
  }
);

export const patchCategory = createAsyncThunk(
  "data/patchCategory",
  async ({
    id,
    categoryData,
  }: {
    id: number;
    categoryData: { name: Record<Language, string> };
  }) => {
    const response = await apiService.patchCategory(id, categoryData);
    return response;
  }
);

export const fetchSubCategories = createAsyncThunk(
  "data/fetchSubCategories",
  async (language: string) => await apiService.fetchSubCategories(language)
);

export const fetchSubCategoriesForList = createAsyncThunk(
  "data/fetchSubCategoriesForList",
  async ({
    language,
    categoryId,
  }: {
    language: string;
    categoryId: number;
  }) => {
    return await apiService.fetchSubCategoriesForList(language, categoryId);
  }
);

export const fetchSubCategoryById = createAsyncThunk(
  "data/fetchSubCategoryById",
  async ({ id, locale }: { id: number; locale: string }) => {
    const response = await apiService.fetchSubCategoryById(id, locale);
    return response;
  }
);

export const postSubCategory = createAsyncThunk(
  "data/postSubCategory",
  async (subCategoryData: {
    categoryId: number;
    name: Record<Language, string>;
  }) => {
    const newSubCategory = await apiService.postSubCategory(subCategoryData);
    return newSubCategory;
  }
);

export const patchSubCategory = createAsyncThunk(
  "data/patchSubCategory",
  async ({
    id,
    subCategoryData,
  }: {
    id: number;
    subCategoryData: { categoryId: number; name: Record<Language, string> };
  }) => {
    const updatedSubCategory = await apiService.patchSubCategory(
      id,
      subCategoryData
    );
    return updatedSubCategory;
  }
);

export const deleteSubCategory = createAsyncThunk(
  "data/deleteSubCategory",
  async (id: number) => {
    await apiService.deleteSubCategory(id);
    return id;
  }
);

export const fetchPackages = createAsyncThunk(
  "data/fetchPackages",
  async ({
    language,
    categoryIds,
    subCategoryIds,
    page,
    count,
    searchQuery,
    order,
  }: {
    language: string;
    categoryIds?: string[];
    subCategoryIds?: string[];
    page?: number;
    count?: number;
    searchQuery?: string;
    order?: string;
  }) => {
    const response = await apiService.fetchPackages(
      language,
      categoryIds,
      subCategoryIds,
      page,
      count,
      searchQuery,
      order
    );
    return response as FetchPackagesResponse;
  }
);

export const fetchThumbnail = createAsyncThunk(
  "data/fetchThumbnail",
  async ({ id }: { id: string }) => {
    const thumbnailUrl = await apiService.fetchThumbnailById(id);
    return { id, thumbnailUrl };
  }
);

export const fetchStepThumbnail = createAsyncThunk(
  "data/fetchStepThumbnail",
  async ({ id }: { id: string }, { rejectWithValue }) => {
    const thumbnailUrl = await apiService.fetchStepThumbnailById(id);
    return { id, thumbnailUrl };
  }
);

export const fetchPackageById = createAsyncThunk(
  "data/fetchPackageById",
  async ({ id, locale }: { id: string; locale: string }) =>
    await apiService.fetchPackageById(id, locale)
);

export const fetchSharedPackageById = createAsyncThunk(
  "data/fetchSharedPackageById",
  async ({
    id,
    locale,
    token,
  }: {
    id: string;
    locale: string;
    token: string;
  }) => await apiService.fetchSharedPackageById(id, locale, token)
);

export const fetchSharedStepThumbnail = createAsyncThunk(
  "data/fetchSharedStepThumbnail",
  async ({ id, token }: { id: string; token: string }) => {
    const thumbnailUrl = await apiService.fetchSharedStepThumbnailById(
      id,
      token
    );
    return { id, thumbnailUrl };
  }
);

export const fetchContentById = createAsyncThunk(
  "data/fetchContentById",
  async ({ id }: { id: string }) => {
    const { url, size } = await apiService.fetchContentById(id);
    return { url, size };
  }
);

export const fetchSharedContentById = createAsyncThunk(
  "data/fetchSharedContentById",
  async ({ id, token }: { id: string; token: string }) => {
    const { url, size } = await apiService.fetchSharedContentById(id, token);
    return { url, size };
  }
);

export const postPackage = createAsyncThunk(
  "data/postPackage",
  async (packageData: {
    subCategoryId: number;
    label: Record<Language, string>;
    description: Record<Language, string>;
  }) => {
    const newPackage = await apiService.postPackage(packageData);
    return newPackage;
  }
);

export const deletePackage = createAsyncThunk(
  "data/deletePackage",
  async (id: number) => {
    await apiService.deletePackage(id);
    return id;
  }
);

export const deletePackageContent = createAsyncThunk(
  "data/deletePackageContent",
  async (id: number) => {
    await apiService.deletePackageContent(id);
    return id;
  }
);

export const deletePackageThumbnail = createAsyncThunk(
  "data/deletePackageThumbnail",
  async (id: number) => {
    await apiService.deletePackageThumbnail(id);
    return id;
  }
);

export const deleteContentThumbnail = createAsyncThunk(
  "data/deleteContentThumbnail",
  async (id: number) => {
    await apiService.deleteContentThumbnail(id);
    return id;
  }
);

export const uploadPackageThumbnail = createAsyncThunk(
  "data/uploadPackageThumbnail",
  async ({ id, thumbnail }: { id: number; thumbnail: File }) => {
    await apiService.uploadThumbnail(id, thumbnail);
  }
);

export const uploadContentThumbnail = createAsyncThunk(
  "data/uploadPackageContentThumbnail",
  async ({ id, thumbnail }: { id: number; thumbnail: File }) => {
    await apiService.uploadContentThumbnail(id, thumbnail);
  }
);

export const uploadContent = createAsyncThunk(
  "data/uploadContent",
  async ({
    id,
    primaryFile,
    secondaryFile,
  }: {
    id: string;
    primaryFile: File;
    secondaryFile?: File;
  }) => {
    await apiService.uploadContent(id, primaryFile, secondaryFile);
    return { id, primaryFile, secondaryFile };
  }
);

export const patchPackageContent = createAsyncThunk(
  "data/patchPackageContent",
  async ({ id, contentData }: { id: number; contentData: PackageContent }) =>
    await apiService.patchPackageContent(id, contentData)
);

export const fetchContentTypes = createAsyncThunk(
  "data/fetchContentTypes",
  async (language: string) => await apiService.fetchContentTypes(language)
);

export const fetchFullPackageById = createAsyncThunk(
  "data/fetchFullPackageById",
  async ({ id, language }: { id: string; language: string }) => {
    const packageData = await apiService.fetchFullPackageById(id, language);
    return packageData;
  }
);

export const fetchStepById = createAsyncThunk(
  "data/fetchStepById",
  async ({ id }: { id: string }) => await apiService.fetchStepById(id)
);

export const fetchLicenses = createAsyncThunk(
  "data/fetchLicenses",
  async () => await apiService.fetchLicenses()
);

export const deleteLicense = createAsyncThunk(
  "data/deleteLicense",
  async (id: number) => {
    await apiService.deleteLicense(id);
    return id;
  }
);

export const fetchLicenseById = createAsyncThunk(
  "data/fetchLicenseById",
  async (id: string) => await apiService.fetchLicenseById(id)
);

export const postLicense = createAsyncThunk(
  "data/postLicense",
  async (licenseData: {
    name: string;
    email: string;
    numberOfUsers: number;
    expiredAt?: string;
  }) => await apiService.postLicense(licenseData)
);

export const patchLicense = createAsyncThunk(
  "data/patchLicense",
  async ({
    id,
    licenseData,
  }: {
    id: number;
    licenseData: {
      name: string;
      email: string;
      numberOfUsers: number;
      expiredAt?: string | null;
    };
  }) => await apiService.patchLicense(id, licenseData)
);

export const patchPackage = createAsyncThunk(
  "data/patchPackage",
  async ({ id, packageData }: { id: string; packageData: PackageRequest }) => {
    const updatedPackage = await apiService.patchPackage(id, packageData);
    return updatedPackage;
  }
);

export const createStep = createAsyncThunk(
  "data/createStep",
  async (stepData: CreateStepData) => {
    const newStep = await apiService.createStep(stepData);
    return newStep;
  }
);

export const fetchLinksForPanoramaCreate = createAsyncThunk(
  "data/fetchLinksForPanorama",
  async ({ id }: { id: string }) => {
    const response = await apiService.fetchLinkForPanoramaCreate(id);
    return response;
  }
);

export const fetchLinkForPanoramaEdit = createAsyncThunk(
  "data/fetchLinksForPackageContent",
  async (
    {
      packageId,
      packageContentId,
    }: { packageId: number; packageContentId?: number },
    { getState }
  ) => {
    const response = await apiService.fetchLinkForPanoramaEdit(
      packageId,
      packageContentId
    );
    return response;
  }
);

export const postLink = createAsyncThunk(
  "data/postLink",
  async (linkData: LinkDataPost) => {
    await apiService.postLink(linkData);
    return linkData;
  }
);

export const patchLink = createAsyncThunk(
  "data/patchLink",
  async ({
    id,
    locale,
    linkData,
  }: {
    id: number;
    locale: string;
    linkData: { targetPackageContentId: number; settings: string };
  }) => {
    await apiService.patchLink(id, locale, linkData);
    return { id, linkData };
  }
);

export const uploadZipFile = createAsyncThunk(
  "data/uploadZipFile",
  async (packagesZip: File) => {
    await apiService.uploadZipFile(packagesZip);
    return { packagesZip };
  }
);

export const uploadCategoryThumbnail = createAsyncThunk(
  "data/uploadCategoryThumbnail",
  async ({ id, thumbnail }: { id: number; thumbnail: File }) => {
    await apiService.uploadCategoryThumbnail(id, thumbnail);
    return { id };
  }
);

interface DataState<T> {
  data: T;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

interface InitialState {
  categories: DataState<Category[]>;
  category: DataState<Category | null>;
  subcategories: DataState<SubCategory[]>;
  fetchSubCategoriesForListStatus: DataState<SubCategory[]>;
  subcategory: DataState<SubCategory | null>;
  postSubCategoryStatus: DataState<SubCategory | null>;
  patchSubCategoryStatus: DataState<SubCategory | null>;
  packages: DataState<Package[]>;
  package: DataState<Package | null>;
  thumbnailUrl: DataState<Record<string, string>>;
  postPackageStatus: DataState<Package | null>;
  contentTypes: DataState<ContentType[]>;
  contentUrl: DataState<string | null>;
  contentData: DataState<{ url: string | null; size: string | null }>;
  totalPackages: number;
  fullPackage: DataState<Package | null>;
  step: DataState<PackageContent | null>;
  licenses: DataState<License[]>;
  postLicenseStatus: DataState<PostLicense | null>;
  license: DataState<License | null>;
  patchLicenseStatus: DataState<PatchLicense | null>;
  createStepStatus: DataState<PackageContent | null>;
  link: DataState<LinksResponse | null>;
  link2: DataState<LinksResponse | null>;
  postLinkStatus: DataState<LinkDataPost | null>;
  uploadContentStatus: DataState<{
    id: string;
    primaryFile: File;
    secondaryFile?: File;
  } | null>;
  patchPackageContentStatus: DataState<PackageContent | null>;
  uploadZipFileStatus: DataState<{ packagesZip: File } | null>;
  uploadCategoryThumbnailStatus: DataState<{ id: number } | null>;
}

const initialState: InitialState = {
  categories: {
    data: [],
    status: "idle",
    error: null,
  },
  category: {
    data: null,
    status: "idle",
    error: null,
  },
  subcategories: {
    data: [],
    status: "idle",
    error: null,
  },
  fetchSubCategoriesForListStatus: {
    data: [],
    status: "idle",
    error: null,
  },
  subcategory: {
    data: null,
    status: "idle",
    error: null,
  },
  postSubCategoryStatus: {
    data: null,
    status: "idle",
    error: null,
  },
  patchSubCategoryStatus: {
    data: null,
    status: "idle",
    error: null,
  },
  packages: {
    data: [],
    status: "idle",
    error: null,
  },
  package: {
    data: null,
    status: "idle",
    error: null,
  },
  thumbnailUrl: {
    data: {},
    status: "idle",
    error: null,
  },
  postPackageStatus: {
    data: null,
    status: "idle",
    error: null,
  },
  contentTypes: {
    data: [],
    status: "idle",
    error: null,
  },
  contentUrl: {
    data: null,
    status: "idle",
    error: null,
  },
  contentData: {
    data: { url: null, size: null },
    status: "idle",
    error: null,
  },
  totalPackages: 0,
  fullPackage: { data: null, status: "idle", error: null },
  step: { data: null, status: "idle", error: null },
  licenses: {
    data: [],
    status: "idle",
    error: null,
  },
  postLicenseStatus: {
    data: null,
    status: "idle",
    error: null,
  },
  license: {
    data: null,
    status: "idle",
    error: null,
  },
  patchLicenseStatus: {
    data: null,
    status: "idle",
    error: null,
  },
  createStepStatus: { data: null, status: "idle", error: null },
  link: { data: null, status: "idle", error: null },
  link2: { data: null, status: "idle", error: null },
  postLinkStatus: { data: null, status: "idle", error: null },
  uploadContentStatus: { data: null, status: "idle", error: null },
  patchPackageContentStatus: { data: null, status: "idle", error: null },
  uploadZipFileStatus: { data: null, status: "idle", error: null },
  uploadCategoryThumbnailStatus: { data: null, status: "idle", error: null },
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.categories.status = "loading";
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories.status = "succeeded";
        state.categories.data = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categories.status = "failed";
        state.categories.error = action.error.message || null;
      })
      .addCase(fetchCategoryById.pending, (state) => {
        state.category.status = "loading";
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.category.status = "succeeded";
        state.category.data = action.payload;
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.category.status = "failed";
        state.category.error = action.error.message || null;
      })
      .addCase(fetchSubCategories.pending, (state) => {
        state.subcategories.status = "loading";
      })
      .addCase(fetchSubCategories.fulfilled, (state, action) => {
        state.subcategories.status = "succeeded";
        state.subcategories.data = action.payload;
      })
      .addCase(fetchSubCategories.rejected, (state, action) => {
        state.subcategories.status = "failed";
        state.subcategories.error = action.error.message || null;
      })
      .addCase(fetchSubCategoriesForList.pending, (state) => {
        state.fetchSubCategoriesForListStatus.status = "loading";
      })
      .addCase(fetchSubCategoriesForList.fulfilled, (state, action) => {
        state.fetchSubCategoriesForListStatus.status = "succeeded";
        state.fetchSubCategoriesForListStatus.data = action.payload;
      })
      .addCase(fetchSubCategoriesForList.rejected, (state, action) => {
        state.fetchSubCategoriesForListStatus.status = "failed";
        state.fetchSubCategoriesForListStatus.error =
          action.error.message || null;
      })
      .addCase(fetchSubCategoryById.pending, (state) => {
        state.subcategories.status = "loading";
      })
      .addCase(fetchSubCategoryById.fulfilled, (state, action) => {
        state.subcategories.status = "succeeded";
        const index = state.subcategories.data.findIndex(
          (subcategory) => subcategory.id === action.payload.id
        );
        if (index === -1) {
          state.subcategories.data.push(action.payload);
        } else {
          state.subcategories.data[index] = action.payload;
        }
      })
      .addCase(fetchSubCategoryById.rejected, (state, action) => {
        state.subcategories.status = "failed";
        state.subcategories.error = action.error.message || null;
      })
      .addCase(postSubCategory.pending, (state) => {
        state.postSubCategoryStatus.status = "loading";
      })
      .addCase(postSubCategory.fulfilled, (state, action) => {
        state.postSubCategoryStatus.status = "succeeded";
        state.postSubCategoryStatus.data = action.payload;
        state.subcategories.data.push(action.payload);
      })
      .addCase(postSubCategory.rejected, (state, action) => {
        state.postSubCategoryStatus.status = "failed";
        state.postSubCategoryStatus.error = action.error.message || null;
      })
      .addCase(patchSubCategory.pending, (state) => {
        state.patchSubCategoryStatus.status = "loading";
      })
      .addCase(patchSubCategory.fulfilled, (state, action) => {
        state.patchSubCategoryStatus.status = "succeeded";
        state.patchSubCategoryStatus.data = action.payload;

        const index = state.subcategories.data.findIndex(
          (sub) => sub.id === action.payload.id
        );
        if (index !== -1) {
          state.subcategories.data[index] = action.payload;
        }
      })
      .addCase(patchSubCategory.rejected, (state, action) => {
        state.patchSubCategoryStatus.status = "failed";
        state.patchSubCategoryStatus.error = action.error.message || null;
      })
      .addCase(fetchPackages.pending, (state) => {
        state.packages.status = "loading";
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.packages.status = "succeeded";
        state.packages.data = action.payload.packages;
        state.totalPackages = action.payload.count;
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.packages.status = "failed";
        state.packages.error = action.error.message || null;
      })
      .addCase(fetchPackageById.pending, (state) => {
        state.package.status = "loading";
      })
      .addCase(fetchPackageById.fulfilled, (state, action) => {
        state.package.status = "succeeded";
        state.package.data = action.payload;
      })
      .addCase(fetchPackageById.rejected, (state, action) => {
        state.package.status = "failed";
        state.package.error = action.error.message || null;
      })
      .addCase(fetchSharedPackageById.pending, (state) => {
        state.package.status = "loading";
      })
      .addCase(fetchSharedPackageById.fulfilled, (state, action) => {
        state.package.status = "succeeded";
        state.package.data = action.payload;
      })
      .addCase(fetchSharedPackageById.rejected, (state, action) => {
        state.package.status = "failed";
        state.package.error = action.error.message || null;
      })
      .addCase(fetchThumbnail.pending, (state) => {
        state.thumbnailUrl.status = "loading";
      })
      .addCase(fetchThumbnail.fulfilled, (state, action) => {
        state.thumbnailUrl.status = "succeeded";
        state.thumbnailUrl.data[action.payload.id] =
          action.payload.thumbnailUrl;
      })
      .addCase(fetchThumbnail.rejected, (state, action) => {
        state.thumbnailUrl.status = "failed";
        state.thumbnailUrl.error = action.error.message || null;
      })
      .addCase(postPackage.pending, (state) => {
        state.postPackageStatus.status = "loading";
      })
      .addCase(postPackage.fulfilled, (state, action) => {
        state.postPackageStatus.status = "succeeded";
        state.postPackageStatus.data = action.payload;
        state.packages.data.push(action.payload);
      })
      .addCase(postPackage.rejected, (state, action) => {
        state.postPackageStatus.status = "failed";
        state.postPackageStatus.error = action.error.message || null;
      })
      .addCase(deletePackage.pending, (state) => {
        state.packages.status = "loading";
      })
      .addCase(deletePackage.fulfilled, (state, action) => {
        state.packages.status = "succeeded";
        state.packages.data = state.packages.data.filter(
          (pkg) => pkg.id !== action.payload
        );
      })
      .addCase(deletePackage.rejected, (state, action) => {
        state.packages.status = "failed";
        state.packages.error = action.error.message || null;
      })
      .addCase(deletePackageContent.pending, (state) => {
        state.packages.status = "loading";
      })
      .addCase(deletePackageContent.fulfilled, (state, action) => {
        state.packages.status = "succeeded";
        state.packages.data = state.packages.data.filter(
          (pkg) => pkg.id !== action.payload
        );
      })
      .addCase(deletePackageContent.rejected, (state, action) => {
        state.packages.status = "failed";
        state.packages.error = action.error.message || null;
      })
      .addCase(uploadPackageThumbnail.pending, (state) => {
        state.thumbnailUrl.status = "loading";
      })
      .addCase(uploadPackageThumbnail.fulfilled, (state) => {
        state.thumbnailUrl.status = "succeeded";
      })
      .addCase(uploadPackageThumbnail.rejected, (state, action) => {
        state.thumbnailUrl.status = "failed";
        state.thumbnailUrl.error = action.error.message || null;
      })
      .addCase(uploadContent.pending, (state) => {
        state.uploadContentStatus.status = "loading";
      })
      .addCase(uploadContent.fulfilled, (state, action) => {
        state.uploadContentStatus.status = "succeeded";
        state.uploadContentStatus.data = action.payload;
      })
      .addCase(uploadContent.rejected, (state, action) => {
        state.uploadContentStatus.status = "failed";
        state.uploadContentStatus.error = action.error.message || null;
      })
      .addCase(uploadContentThumbnail.pending, (state) => {
        state.thumbnailUrl.status = "loading";
      })
      .addCase(uploadContentThumbnail.fulfilled, (state) => {
        state.thumbnailUrl.status = "succeeded";
      })
      .addCase(uploadContentThumbnail.rejected, (state, action) => {
        state.thumbnailUrl.status = "failed";
        state.thumbnailUrl.error = action.error.message || null;
      })
      .addCase(fetchContentTypes.pending, (state) => {
        state.contentTypes.status = "loading";
      })
      .addCase(fetchContentTypes.fulfilled, (state, action) => {
        state.contentTypes.status = "succeeded";
        state.contentTypes.data = action.payload;
      })
      .addCase(fetchContentTypes.rejected, (state, action) => {
        state.contentTypes.status = "failed";
        state.contentTypes.error = action.error.message || null;
      })
      .addCase(fetchStepThumbnail.pending, (state) => {
        state.thumbnailUrl.status = "loading";
      })
      .addCase(fetchStepThumbnail.fulfilled, (state, action) => {
        state.thumbnailUrl.status = "succeeded";
        state.thumbnailUrl.data[action.payload.id] =
          action.payload.thumbnailUrl;
      })
      .addCase(fetchStepThumbnail.rejected, (state, action) => {
        state.thumbnailUrl.status = "failed";
        state.thumbnailUrl.error = action.error.message || null;
      })
      .addCase(fetchSharedStepThumbnail.pending, (state) => {
        state.thumbnailUrl.status = "loading";
      })
      .addCase(fetchSharedStepThumbnail.fulfilled, (state, action) => {
        state.thumbnailUrl.status = "succeeded";
        state.thumbnailUrl.data[action.payload.id] =
          action.payload.thumbnailUrl;
      })
      .addCase(fetchSharedStepThumbnail.rejected, (state, action) => {
        state.thumbnailUrl.status = "failed";
        state.thumbnailUrl.error = action.error.message || null;
      })
      .addCase(fetchContentById.pending, (state) => {
        state.contentData.status = "loading";
      })
      .addCase(fetchContentById.fulfilled, (state, action) => {
        state.contentData.status = "succeeded";
        state.contentData.data.url = action.payload.url;
        state.contentData.data.size = action.payload.size;
      })
      .addCase(fetchContentById.rejected, (state, action) => {
        state.contentData.status = "failed";
        state.contentData.error = action.error.message || null;
      })
      .addCase(fetchSharedContentById.pending, (state) => {
        state.contentData.status = "loading";
      })
      .addCase(fetchSharedContentById.fulfilled, (state, action) => {
        state.contentData.status = "succeeded";
        state.contentData.data.url = action.payload.url;
        state.contentData.data.size = action.payload.size;
      })
      .addCase(fetchSharedContentById.rejected, (state, action) => {
        state.contentData.status = "failed";
        state.contentData.error = action.error.message || null;
      })
      .addCase(fetchFullPackageById.pending, (state) => {
        state.fullPackage.status = "loading";
      })
      .addCase(fetchFullPackageById.fulfilled, (state, action) => {
        state.fullPackage.status = "succeeded";
        state.fullPackage.data = action.payload;
      })
      .addCase(fetchFullPackageById.rejected, (state, action) => {
        state.fullPackage.status = "failed";
        state.fullPackage.error = action.error.message || null;
      })
      .addCase(fetchStepById.pending, (state) => {
        state.step.status = "loading";
      })
      .addCase(fetchStepById.fulfilled, (state, action) => {
        state.step.status = "succeeded";
        state.step.data = action.payload;
      })
      .addCase(fetchStepById.rejected, (state, action) => {
        state.step.status = "failed";
        state.step.error = action.error.message || null;
      })
      .addCase(patchPackage.pending, (state) => {
        state.package.status = "loading";
      })
      .addCase(patchPackage.fulfilled, (state, action) => {
        state.package.status = "succeeded";
        const index = state.packages.data.findIndex(
          (pkg) => pkg.id === action.payload.id
        );
        if (index !== -1) {
          state.packages.data[index] = action.payload;
        }
      })
      .addCase(patchPackage.rejected, (state, action) => {
        state.package.status = "failed";
        state.package.error = action.error.message || null;
      })
      .addCase(createStep.pending, (state) => {
        state.createStepStatus.status = "loading";
      })
      .addCase(createStep.fulfilled, (state, action) => {
        state.createStepStatus.status = "succeeded";
        state.createStepStatus.data = action.payload;
      })
      .addCase(createStep.rejected, (state, action) => {
        state.createStepStatus.status = "failed";
        state.createStepStatus.error = action.error.message || null;
      })
      .addCase(fetchLinksForPanoramaCreate.pending, (state) => {
        state.link.status = "loading";
      })
      .addCase(fetchLinksForPanoramaCreate.fulfilled, (state, action) => {
        state.link.status = "succeeded";
        state.link.data = action.payload;
      })
      .addCase(fetchLinksForPanoramaCreate.rejected, (state, action) => {
        state.link.status = "failed";
        state.link.error = action.error.message || null;
      })
      .addCase(fetchLinkForPanoramaEdit.pending, (state) => {
        state.link.status = "loading";
      })
      .addCase(fetchLinkForPanoramaEdit.fulfilled, (state, action) => {
        state.link.status = "succeeded";
        state.link.data = action.payload;
      })
      .addCase(fetchLinkForPanoramaEdit.rejected, (state, action) => {
        state.link.status = "failed";
        state.link.error = action.error.message || null;
      })
      .addCase(postLink.pending, (state) => {
        state.postLinkStatus.status = "loading";
      })
      .addCase(postLink.fulfilled, (state, action) => {
        state.postLinkStatus.status = "succeeded";
        state.postLinkStatus.data = action.payload;
      })
      .addCase(postLink.rejected, (state, action) => {
        state.postLinkStatus.status = "failed";
        state.postLinkStatus.error = action.error.message || null;
      })
      .addCase(patchLink.pending, (state) => {
        state.link.status = "loading";
      })
      .addCase(patchLink.fulfilled, (state, action) => {
        state.link.status = "succeeded";
        state.link.data = [
          {
            targetPackageContentId:
              action.payload.linkData.targetPackageContentId,
            label: action.payload.linkData.settings,
          },
        ];
      })
      .addCase(patchLink.rejected, (state, action) => {
        state.link.status = "failed";
        state.link.error = action.error.message || null;
      })
      .addCase(fetchLicenses.pending, (state) => {
        state.licenses.status = "loading";
      })
      .addCase(fetchLicenses.fulfilled, (state, action) => {
        state.licenses.status = "succeeded";
        state.licenses.data = action.payload;
      })
      .addCase(fetchLicenses.rejected, (state, action) => {
        state.licenses.status = "failed";
        state.licenses.error = action.error.message || null;
      })
      .addCase(deleteLicense.pending, (state) => {
        state.licenses.status = "loading";
      })
      .addCase(deleteLicense.fulfilled, (state, action) => {
        state.licenses.status = "succeeded";
        state.licenses.data = state.licenses.data.filter(
          (license) => license.id !== action.payload
        );
      })
      .addCase(deleteLicense.rejected, (state, action) => {
        state.licenses.status = "failed";
        state.licenses.error = action.error.message || null;
      })
      .addCase(postLicense.pending, (state) => {
        state.postLicenseStatus.status = "loading";
      })
      .addCase(postLicense.fulfilled, (state, action) => {
        state.postLicenseStatus.status = "succeeded";
        state.postLicenseStatus.data = action.payload;
        state.licenses.data.push(action.payload);
      })
      .addCase(postLicense.rejected, (state, action) => {
        state.postLicenseStatus.status = "failed";
        state.postLicenseStatus.error = action.error.message || null;
      })
      .addCase(fetchLicenseById.pending, (state) => {
        state.license.status = "loading";
      })
      .addCase(fetchLicenseById.fulfilled, (state, action) => {
        state.license.status = "succeeded";
        state.license.data = action.payload;
      })
      .addCase(fetchLicenseById.rejected, (state, action) => {
        state.license.status = "failed";
        state.license.error = action.error.message || null;
      })
      .addCase(patchLicense.pending, (state) => {
        state.patchLicenseStatus.status = "loading";
      })
      .addCase(patchLicense.fulfilled, (state, action) => {
        state.patchLicenseStatus.status = "succeeded";
        state.patchLicenseStatus.data = action.payload;
      })
      .addCase(patchLicense.rejected, (state, action) => {
        state.patchLicenseStatus.status = "failed";
        state.patchLicenseStatus.error = action.error.message || null;
      })
      .addCase(patchPackageContent.pending, (state) => {
        state.patchPackageContentStatus.status = "loading";
      })
      .addCase(patchPackageContent.fulfilled, (state, action) => {
        state.patchPackageContentStatus.status = "succeeded";
        state.patchPackageContentStatus.data = action.payload;

        const index = state.packages.data.findIndex(
          (pkg) => pkg.id === action.payload.id
        );
        if (index !== -1) {
          state.packages.data[index] = {
            ...state.packages.data[index],
            ...action.payload,
          };
        }
      })
      .addCase(patchPackageContent.rejected, (state, action) => {
        state.patchPackageContentStatus.status = "failed";
        state.patchPackageContentStatus.error = action.error.message || null;
      })
      .addCase(postCategory.pending, (state) => {
        state.categories.status = "loading";
      })
      .addCase(postCategory.fulfilled, (state, action) => {
        state.categories.status = "succeeded";
        state.categories.data.push(action.payload);
      })
      .addCase(postCategory.rejected, (state, action) => {
        state.categories.status = "failed";
        state.categories.error = action.error.message || null;
      })
      .addCase(patchCategory.pending, (state) => {
        state.category.status = "loading";
      })
      .addCase(patchCategory.fulfilled, (state, action) => {
        state.category.status = "succeeded";
        state.category.data = action.payload;

        const index = state.categories.data.findIndex(
          (cat) => cat.id === action.payload.id
        );
        if (index !== -1) {
          state.categories.data[index] = action.payload;
        }
      })
      .addCase(patchCategory.rejected, (state, action) => {
        state.category.status = "failed";
        state.category.error = action.error.message || null;
      })
      .addCase(deleteCategory.pending, (state) => {
        state.categories.status = "loading";
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.categories.status = "succeeded";
        state.categories.data = state.categories.data.filter(
          (category) => category.id !== action.payload
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.categories.status = "failed";
        state.categories.error = action.error.message || null;
      })
      .addCase(deleteSubCategory.pending, (state) => {
        state.subcategories.status = "loading";
      })
      .addCase(deleteSubCategory.fulfilled, (state, action) => {
        state.subcategories.status = "succeeded";
        state.subcategories.data = state.subcategories.data.filter(
          (subcategory) => subcategory.id !== action.payload
        );
      })
      .addCase(deleteSubCategory.rejected, (state, action) => {
        state.subcategories.status = "failed";
        state.subcategories.error = action.error.message || null;
      })
      .addCase(uploadZipFile.pending, (state) => {
        state.uploadZipFileStatus.status = "loading";
      })
      .addCase(uploadZipFile.fulfilled, (state, action) => {
        state.uploadZipFileStatus.status = "succeeded";
        state.uploadZipFileStatus.data = action.payload;
      })
      .addCase(uploadZipFile.rejected, (state, action) => {
        state.uploadZipFileStatus.status = "failed";
        state.uploadZipFileStatus.error = action.error.message || null;
      })
      .addCase(uploadCategoryThumbnail.pending, (state) => {
        state.uploadCategoryThumbnailStatus.status = "loading";
      })
      .addCase(uploadCategoryThumbnail.fulfilled, (state, action) => {
        state.uploadCategoryThumbnailStatus.status = "succeeded";
        state.uploadCategoryThumbnailStatus.data = action.payload;
      })
      .addCase(uploadCategoryThumbnail.rejected, (state, action) => {
        state.uploadCategoryThumbnailStatus.status = "failed";
        state.uploadCategoryThumbnailStatus.error =
          action.error.message || null;
      });
  },
});

export default dataSlice.reducer;
